import { useState, FC, useEffect } from 'react';
import { Icon, AccordionTable, CollapseTableData, Avatar, UiLibrary, Tooltip } from 'learning-panel-components';
import classNames from 'classnames';
import { translate } from 'translations';
import InnerTableContainer from './innerTableContainer';
import styles from './teamLeaderboard.module.scss';

export const getRow = (
    row: RowData,
    index: number,
    membersNumberText: string,
    outOfText?: string,
    tooltipText?: string,
    smallerRank?: boolean,
    isNewLayout?: boolean
) => {
    const rowRank = Number(row.rank);
    const isTopThree = rowRank === 1 || rowRank === 2 || rowRank === 3;
    const rankElement = isNewLayout ? (
        <div className={styles['rank-wrapper-new']}>#{rowRank}</div>
    ) : (
        <div
            className={classNames(styles['rank-wrapper'], {
                [styles['first-row']]: index === 0,
                [styles['rank-width']]: smallerRank
            })}
        >
            {isTopThree && (
                <img
                    alt="challengeTeamLeaderboard"
                    className={styles['top-rank']}
                    src={`${process.env.PUBLIC_URL}/images/topRank.svg`}
                />
            )}
            <div
                className={classNames(styles['row-rank'], {
                    [styles['first-row']]: index === 0,
                    [styles['row-rank-width']]: smallerRank
                })}
            >{`#${row.rank}`}</div>
        </div>
    );
    const nameElement = (
        <div className={classNames(styles['row-name'], { [styles['first-row']]: index === 0 })}>
            <Avatar name={row.name} avatarSrc={row.profilePicture} size={isNewLayout ? '1.7rem' : undefined} />
            <div
                title={row.name}
                className={styles.text}
                style={{ '--maxWidth': isNewLayout ? '5rem' : '9rem' } as any}
            >
                {row.name}
            </div>
        </div>
    );
    const teamMembersElement = (
        <div
            onClick={() => row.changeOpenMode()}
            className={classNames(styles['row-members'], { [styles['first-row']]: index === 0 })}
        >
            {tooltipText ? (
                <Tooltip
                    title={`${row.usersCount} ${outOfText} ${row.teamMembers} ${tooltipText}`}
                    placement="top"
                    interactive
                >
                    <div>{`${row.usersCount}/${row.teamMembers} ${membersNumberText}`}</div>
                </Tooltip>
            ) : (
                <div>{`${row.teamMembers} ${membersNumberText}`}</div>
            )}

            <UiLibrary.ExpandMore />
        </div>
    );
    const scoreElement = (
        <div className={styles['row-score']}>
            <Icon name={'teamCoins'} className={styles['dev-coin']} />
            <div>{row.score?.toLocaleString()}</div>
        </div>
    );
    return [rankElement, nameElement, teamMembersElement, scoreElement];
};

const TeamLeaderboard: FC<TeamLeaderboardProps> = (props: TeamLeaderboardProps) => {
    const { teams, myTeams, onLoadMore, hasMore, teamsUsersOpenState, onChangeOpenMode, challengeUuid } = props;

    const [allTeamsRows, setAllTeamsRows] = useState<CollapseTableData[]>([]);
    const [myTeamsRows, setMyTeamsRows] = useState<CollapseTableData[]>([]);
    const [myTeamsIsOpen, setMyTeamsIsOpen] = useState<boolean>(true);
    const [allTeamsIsOpen, setAllTeamsIsOpen] = useState<boolean>(true);
    const membersNumberText = translate('members');
    const myTeamsText = translate('myTeams');
    const allTeamsText = translate('allTeams');
    const outOfText = translate('outOf');
    const tooltipText = translate('membersChallengeTooltip');

    const mapTeam = (teams: TeamData[], teamType: string) =>
        teams?.map((team: TeamData, i: number) => ({
            row: getRow(
                {
                    rank: team.rank,
                    name: team.teamName,
                    score: team.score,
                    teamMembers: team.teamMembers,
                    usersCount: team.usersCount,
                    profilePicture: team.profilePicture,
                    changeOpenMode: () => onChangeOpenMode(team.uuid, teamType)
                },
                teamType === 'all' ? i : -1,
                membersNumberText,
                outOfText,
                tooltipText
            ),
            innerTable: (
                <InnerTableContainer
                    isOpen={teamsUsersOpenState[`${team.uuid}-${teamType}`]}
                    teamUuid={team.uuid}
                    challengeUuid={challengeUuid}
                />
            ),
            isCollapseAble: true,
            isOpen: teamsUsersOpenState[`${team.uuid}-${teamType}`]
        }));

    useEffect(() => {
        setAllTeamsRows(mapTeam(teams, 'all'));
    }, [teams, teamsUsersOpenState]);

    useEffect(() => {
        setMyTeamsRows(mapTeam(myTeams, 'my'));
    }, [myTeams, teamsUsersOpenState]);

    return (
        <div>
            {myTeamsRows.length > 0 && (
                <AccordionTable
                    rows={myTeamsRows}
                    title={myTeamsText}
                    isOpen={true}
                    hasMoreRows={hasMore}
                    loadMoreRows={onLoadMore}
                    tableHeight={allTeamsIsOpen ? '14rem' : '28rem'}
                    setIsOpen={(isOpen: boolean) => setMyTeamsIsOpen(isOpen)}
                    titleCount={myTeams.length}
                />
            )}
            {allTeamsRows.length > 0 && (
                <AccordionTable
                    rows={allTeamsRows}
                    title={allTeamsText}
                    isOpen={true}
                    hasMoreRows={hasMore}
                    loadMoreRows={onLoadMore}
                    tableHeight={myTeamsRows.length > 0 && myTeamsIsOpen ? '14rem' : '28rem'}
                    setIsOpen={(isOpen: boolean) => setAllTeamsIsOpen(isOpen)}
                    titleCount={allTeamsRows.length}
                />
            )}
        </div>
    );
};

interface TeamLeaderboardProps {
    teams: TeamData[];
    myTeams: TeamData[];
    onLoadMore: Function;
    hasMore: boolean;
    teamsUsersOpenState: { [teamUuid: string]: boolean };
    onChangeOpenMode: Function;
    challengeUuid?: string;
}

export interface TeamData {
    uuid: string;
    rank: number;
    teamName: string;
    teamMembers: number;
    usersCount: number;
    score: number;
    profilePicture?: string;
}

export interface RowData {
    rank: number;
    name: string;
    teamMembers?: number;
    usersCount?: number;
    score: number;
    profilePicture?: string;
    changeOpenMode: Function;
}

export default TeamLeaderboard;
